// @ts-strict-ignore
import { ReactNode, useCallback, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { Editor, Descendant, createEditor } from 'slate';
import { withReact } from 'slate-react';
import { withHistory } from 'slate-history';
import { useDispatch, useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Origin, Section } from 'src/constants/footfall';
import { createPost, editPost, getTags } from 'src/modules/community/write/actions';
import { showModal } from 'src/modules/shared/modal/actions';
import { ModalType } from 'src/constants/modalTypes';
import { validateBody, validateTitle } from 'src/modules/community/write/validate';
import { useUnsavedChangesPrompt } from 'src/utils/hooks/useUnsavedChangesPrompt';
import { getLocalStorageItem, Key } from 'src/utils/localStorage';
import { helpScoutArticle } from 'src/utils/helpScoutBeacon';
import { withMentions } from 'src/components/textEditor/components/mentionsPlugins';
import { withImages } from 'src/components/textEditor/components/imagesPlugin';
import { withTitleLayout } from 'src/modules/community/write/components/titlePlugin';
import { MAX_CHARS_POST_BODY, MAX_CHARS_POST_TITLE } from 'src/constants/write';
import { MyEditor, useEditor } from 'src/components/textEditor/customHelpers';
import { getEditorData, serialise } from 'src/components/textEditor/serialiseContent';
import { useDeserialiseHtmlString } from 'src/components/textEditor/deserialiseContent';
import RichTextEditor from 'src/components/textEditor/richTextEditor';
import EditorToolbar from 'src/components/textEditor/components/editorToolbar';
import Spacer from 'src/components/spacer';
import { EMAIL_IN_TEXT, PHONE_NUMBER, URL_RE } from 'src/constants/regex';
import { Counter, EditorContainer, SubmitButtonWrapper, PostContainer, SubmitButton, TipsButton, TipsButtonContainer } from 'src/modules/community/write/components/sharedStyles';
import WritePostErrors from 'src/modules/community/write/components/writePostErrors';
const initialValuePost: Descendant[] = [{
  type: 'paragraph',
  children: [{
    text: ''
  }]
}];
const initialValueTitle: Descendant[] = [{
  type: 'title-medium',
  children: [{
    text: ''
  }]
}];
export default function WritePostEditor({
  children
}: {
  children: ReactNode;
}): JSX.Element {
  const dispatch = useDispatch();
  const {
    post,
    htmlTitleString,
    htmlBodyString
  } = useSelector(state => state.pages.write);
  const editorTitle = useEditor(withTitleLayout(withReact(withHistory(createEditor()))));
  const editorPost = useEditor(withImages(withMentions(withReact(withHistory(createEditor())))));
  const postContainerRef = useRef<HTMLDivElement>(null);
  const [titleCounter, setTitleCounter] = useState(0);
  const countCharsTitle = (editor: Editor) => {
    const charCount = MyEditor.getString(editor, []).length;
    setTitleCounter(charCount);
  };
  const debouncedGetTags = useCallback(debounce((title, body) => {
    dispatch(getTags(title, body));
  }, 5000), []);
  const onChangeGetTags = () => {
    const titleString = editorTitle.children.length && MyEditor.getString(editorTitle, []);
    const postString = editorPost && editorPost.children.length && serialise(editorPost);
    if (titleString?.length >= 5 && postString?.length >= 20) {
      debouncedGetTags(titleString, postString);
    }
  };
  const [errors, setErrors] = useState({
    title: [],
    body: []
  });
  const [isDirty, setIsDirty] = useState({
    title: false,
    body: false
  });
  const debouncedValidateTitle = useCallback(debounce(() => {
    const titleErrors = validateTitle({
      editor: editorTitle,
      setIsDirty: newState => setIsDirty(prevState => ({
        ...prevState,
        title: newState
      }))
    });
    setErrors(prevState => ({
      ...prevState,
      title: titleErrors
    }));
  }, 500), []);
  const debouncedValidateBody = useCallback(debounce(() => {
    const bodyErrors = validateBody({
      editor: editorPost,
      setIsDirty: newState => setIsDirty(prevState => ({
        ...prevState,
        body: newState
      }))
    });
    setErrors(prevState => ({
      ...prevState,
      body: bodyErrors
    }));
  }, 500), []);
  const trackSubmit = () => {
    trackFootfallEvent(Event.Clicked, {
      clickMetadata: {
        origin: post?.id ? Origin.EditPostPage : Origin.WritePostPage
      },
      clickType: 'submit'
    });
  };
  const submitPost = () => {
    // both create and edit
    const serialisedTitle = serialise(editorTitle);
    const {
      image,
      text,
      mentionedUserIds
    } = getEditorData(editorPost);
    const revalidateErrorsTitle = validateTitle({
      editor: editorTitle
    });
    const revalidateErrorsBody = validateBody({
      editor: editorPost
    });
    if (revalidateErrorsTitle.length || revalidateErrorsBody.length && !revalidateErrorsBody[0].submitAllowed) {
      setErrors({
        body: revalidateErrorsBody,
        title: revalidateErrorsTitle
      });
    } else {
      setIsDirty({
        title: false,
        body: false
      });
      const doSubmit = post?.id // edit post
      ? editPost({
        title: serialisedTitle,
        body: text,
        mentionedUserIds,
        image
      }) : createPost({
        title: serialisedTitle,
        body: text,
        mentionedUserIds,
        image
      });
      if (!getLocalStorageItem(Key.privacyReminderDismissed) && (EMAIL_IN_TEXT.test(serialisedTitle) || PHONE_NUMBER.test(serialisedTitle) && !URL_RE.test(serialisedTitle) || EMAIL_IN_TEXT.test(text) || PHONE_NUMBER.test(text) && !URL_RE.test(text) || !!image?.file)) {
        // only triggers modal when new image is being uploaded
        return dispatch(showModal({
          modalType: ModalType.PrivacyReminder,
          modalProps: {
            onAccept: () => {
              dispatch(doSubmit);
              trackSubmit();
            },
            data: {
              text: serialisedTitle + ' ' + text,
              image
            } // the space is needed for better matches in the modal
          }
        }));
      }
      dispatch(doSubmit);
      trackSubmit();
    }
  };
  const editTitle = useDeserialiseHtmlString({
    text: htmlTitleString
  });
  const editBody = useDeserialiseHtmlString({
    text: htmlBodyString,
    mentionedUsers: post?.mentionedUsers,
    image: {
      url: post?.imageUrl,
      hash: post?.postImageHash
    }
  });
  useUnsavedChangesPrompt({
    unsavedChanges: isDirty.title || isDirty.body
  });
  return <>
      <EditorContainer $hasErrors={!!errors.title?.length} aria-label="Text editor for post title" data-testid="write-post-title" data-sentry-element="EditorContainer" data-sentry-source-file="writePostEditor.tsx">
        {(!post?.id || post?.id && editTitle) && <RichTextEditor editor={editorTitle} hasHotkeyFormatting={false} hasMentions={false} initialValue={(editTitle as Descendant[]) || initialValueTitle} maxChars={MAX_CHARS_POST_TITLE} placeholder={i18n.t('Title\n')} // if placeholder ends with a word, autosuggest picks it up; can force insert with some androids
      onChange={(editor: Editor) => {
        countCharsTitle(editor);
        onChangeGetTags();
        debouncedValidateTitle();
      }}>
            <Counter>{titleCounter}/140</Counter>
            <TipsButtonContainer>
              <TipsButton dataTestId="privacy-tips-button" icon="information" text={i18n.t('Post tips')} type="link" onClick={() => {
            helpScoutArticle('190-posting-tips');
            trackFootfallEvent(Event.Clicked, {
              clickMetadata: {
                section: Section.WritePostTips,
                origin: post?.id ? Origin.EditPostPage : Origin.WritePostPage
              },
              clickType: 'viewed'
            });
          }} />
            </TipsButtonContainer>
          </RichTextEditor>}
      </EditorContainer>
      <WritePostErrors dataTestId="write-post-title-error" errors={errors.title} data-sentry-element="WritePostErrors" data-sentry-source-file="writePostEditor.tsx" />
      <Spacer data-sentry-element="Spacer" data-sentry-source-file="writePostEditor.tsx" />
      <PostContainer $hasErrors={!!errors.body?.length} aria-label="Text editor for post body" data-testid="write-post-body" ref={postContainerRef} data-sentry-element="PostContainer" data-sentry-source-file="writePostEditor.tsx">
        {(!post?.id || post?.id && editBody) && <RichTextEditor autoFocus={false} editor={editorPost} hasMentions={true} imgWithDescription={false} initialValue={(editBody as Descendant[]) || initialValuePost} maxChars={MAX_CHARS_POST_BODY} placeholder={i18n.t('Tell your story...')} toolbar={<EditorToolbar dataTestId="editor-toolbar" onAddImg={() => postContainerRef.current?.scrollIntoView({
        behavior: 'smooth'
      })} />} onChange={() => {
        onChangeGetTags();
        debouncedValidateBody();
      }} onKeyDown={MyEditor.addSoftBreak} />}
      </PostContainer>
      <WritePostErrors dataTestId="write-post-body-error" errors={errors.body} data-sentry-element="WritePostErrors" data-sentry-source-file="writePostEditor.tsx" />
      {children}
      <SubmitButtonWrapper data-sentry-element="SubmitButtonWrapper" data-sentry-source-file="writePostEditor.tsx">
        <SubmitButton ariaLabel="Submit post" dataTestId="submit-post-button" disabled={!!errors.body?.length && !errors.body[0]?.submitAllowed || !!errors.title?.length} role="submit" onClick={submitPost} data-sentry-element="SubmitButton" data-sentry-source-file="writePostEditor.tsx">
          {i18n.t('Post')}
        </SubmitButton>
      </SubmitButtonWrapper>
    </>;
}